import {
    call,
    put,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
    takeLeading,
} from 'redux-saga/effects';

import logger from '@elab/core/utils/logger';
import animationHelpers from '@elab/core/utils/ui/animationHelpers';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import { getOrigin, pushState } from '@elab/core/utils/address';
import {
    deliveryDateServerErrors,
    dialogWithProductDetailsName,
    rtsDialogName,
    storageNames,
} from '@elab/core/utils/constants';
import * as storage from '@elab/core/utils/storage';
import {
    actions as uiActions,
    selectors as uiSelectors,
    types as uiTypes,
} from '@elab/core/store/ui';
import rtsTypes from '@elab/components/connected/real-time-stock/redux/types';
import rtsActions from '@elab/components/connected/real-time-stock/redux/actions';
import { actions as reviewsActions } from '@elab/core/store/reviews';
import analytics from '@elab/core/utils/analytics';

import { actions as cartActions } from '../cart';
import { selectors as configSelectors } from '../config';
import { getCookie, setCookie } from '../../utils/cookie';
import { selectors as userSelectors } from '../user';

import * as api from './api';

import { actions, selectors, types } from '.';

const storeName = 'product';
const eGiftCardProduct = 'E-Gift Card';
const lastViewedProductsCookieName = 'lastviewedproducts';

export function* loadProduct({ payload: { code, propStoreName } }) {
    const loadedProductCode = yield select(selectors.getProductCode, propStoreName || storeName);
    if (loadedProductCode !== code) {
        yield put(actions.setLoading(true));
        try {
            const response = yield call(api.loadProductVariantMatrix, code);
            const useMarkDownPromotion = yield select(configSelectors.useMarkDownPromotionValue); // BU specific feature config to use markdown
            const isOosPromoBadge = yield select(configSelectors.isOosPromoBadge); // BU specific feature config to show promo badge for oos products
            const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
            const isDeliveryCountry = yield select(configSelectors.isDeliveryCountry);
            let updatedResponse;

            if (isDeliveryCountry) {
                updatedResponse =
                    useMarkDownPromotion && isOosPromoBadge
                        ? { ...response, useMarkDownPromotion, isOosPromoBadge, deliveryCountry }
                        : { ...response, deliveryCountry };
            } else {
                updatedResponse =
                    useMarkDownPromotion && isOosPromoBadge
                        ? { ...response, useMarkDownPromotion, isOosPromoBadge }
                        : response;
            }
            yield put(actions.setProduct(updatedResponse));
            const thresholdPricingEnabled = yield select(globalSelectors.getThresholdFeatureFlag);
            console.log('thresholdPricingEnabled', thresholdPricingEnabled);
            if (thresholdPricingEnabled === 'true') {
                yield call(checkThresholdPromotion, updatedResponse.promotions);
            } else {
                yield put(actions.setThresholdPromotion({}));
            }
            const forceClick = yield select(uiSelectors.getForceOpen);
            const selectedQuantity = yield select(uiSelectors.getselectedQuantity);
            const productRangeName = yield select(selectors.getProductRangeName, storeName);
            if (productRangeName == eGiftCardProduct) {
                yield put(cartActions.loadCart());
            }
            if (forceClick) {
                yield put(rtsActions.showProductStock({ name: 'rts-pickup', storeName }));
                yield put(actions.setQuantity(selectedQuantity));
            }
        } catch (e) {
            yield spawn(logger.error, e);
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

function* checkThresholdPromotion(promotions) {
    const thresholdPromotions = promotions.filter(item => !!item.minimumPurchaseAmount);
    const thresholdLabel = yield select(globalSelectors.getThresholdTextLabel);
    let modifiedThresholdPromotions = {};
    if (thresholdPromotions && thresholdPromotions.length > 0) {
        modifiedThresholdPromotions = {
            ...thresholdPromotions[0],
            textLabel: thresholdLabel.replace(
                '{0}',
                thresholdPromotions[0].minimumPurchaseAmount.value,
            ),
        };
    }
    yield put(actions.setThresholdPromotion(modifiedThresholdPromotions));
}

export function* handleProductView({ selectors, types, storeName }) {
    const shouldSendVariantEvent = yield select(configSelectors.shouldSendVariantEvent); // Set this variant to send event when variant changes
    if (!shouldSendVariantEvent) {
        return;
    }

    yield take(types.UPDATE_PRODUCT_WITH_VARIANT_PRICE_DATA);
    const product = yield select(selectors.getProductDetails, storeName);

    const collectStockInfo = yield select(configSelectors.collectStockInfo);

    const stockInfo = {};
    if (collectStockInfo) {
        stockInfo.inStock = product.stock && product.stock.stockLevelStatus === 'inStock';
        stockInfo.stockLevel = (product.stock && product.stock.stockLevel) || '';
    }
    const isGuest = yield select(userSelectors.isAnonymous);
    yield call(analytics.handleView, {
        event: 'genEcom',
        type: 'pdp-view',
        label: 'product detail page',
        code: product.code,
        name: product.rangeName || product.name,
        range: product.rangeName,
        price: product.price.value,
        brand: product.masterBrand && product.masterBrand.name,
        category: product.categoryNameHierarchy,
        variant: `${product.name}${
            product.variantCategoryName ? ` ${product.variantCategoryName}` : ''
        }`,
        baseProduct: product.baseProduct,
        supplier: product.supplierDescription,
        isGuest,
        ...stockInfo,
    });
}

export function* loadProductVariantData(
    { actions, selectors, types, storeName },
    { payload: { code, refresh, url } },
) {
    const currentSelectedVariantCode = yield select(selectors.getProductCode, storeName);

    if (code === currentSelectedVariantCode && !refresh) return;

    yield put(actions.setLoading(true));
    yield spawn(handleProductView, { selectors, types, storeName });
    try {
        const response = yield call(api.loadProductVariant, code);
        const useMarkDownPromotion = yield select(configSelectors.useMarkDownPromotionValue); // BU specific feature config to use markdown
        const isOosPromoBadge = yield select(configSelectors.isOosPromoBadge); // BU specific feature config to show promo badge for oos products
        const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);

        const isDeliveryCountry = yield select(configSelectors.isDeliveryCountry);
        let updatedResponse;

        if (isDeliveryCountry) {
            updatedResponse =
                useMarkDownPromotion && isOosPromoBadge
                    ? {
                          code,
                          variantData: response,
                          useMarkDownPromotion,
                          isOosPromoBadge,
                          deliveryCountry,
                      }
                    : {
                          code,
                          variantData: response,
                          deliveryCountry,
                      };
        } else {
            updatedResponse =
                useMarkDownPromotion && isOosPromoBadge
                    ? {
                          code,
                          variantData: response,
                          useMarkDownPromotion,
                          isOosPromoBadge,
                          deliveryCountry,
                      }
                    : {
                          code,
                          variantData: response,
                          deliveryCountry,
                      };
        }
        yield put(actions.updateProductWithVariantData(updatedResponse));
        const shouldLoadReviewOnVariants = yield select(configSelectors.shouldLoadReviewOnVariants);

        if (shouldLoadReviewOnVariants) {
            yield put(reviewsActions.setProductCode(code));
        }

        if (storeName === 'product' && url) {
            pushState(url);
        }
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* loadProductHomeDeliveryDate({ payload: { deliveryMethod, productCode } }) {
    try {
        const response = yield call(api.getProductHomeDeliveryDate, deliveryMethod, productCode);
        yield put(actions.setProductHomeDeliveryDate(response));
    } catch ({ message }) {
        if (message === deliveryDateServerErrors.OUT_OF_STOCK_FOR_HOME_DELIVERY) {
            yield put(
                actions.setProductHomeDeliveryDate({
                    isOutOfStock: true,
                }),
            );
        }
    }
}

export function* loadProductPickUpInStoreDeliveryDate({
    payload: { deliveryMethod, productCode },
}) {
    const lastUsedStore = yield call(storage.get, storageNames.lastUsedStore);
    let latitude = 0;
    let longitude = 0;

    if (lastUsedStore) {
        try {
            const lastUserStoreObj = JSON.parse(lastUsedStore);
            latitude = lastUserStoreObj.geoPoint && lastUserStoreObj.geoPoint.latitude;
            longitude = lastUserStoreObj.geoPoint && lastUserStoreObj.geoPoint.longitude;
        } catch (e) {
            yield spawn(logger.error, e);
        }
    }

    try {
        const response = yield call(
            api.getProductPickupInStoreDeliveryDate,
            deliveryMethod,
            productCode,
            latitude,
            longitude,
        );
        yield put(actions.setProductPickupInStoreDeliveryDate(response));
    } catch ({ message }) {
        if (
            message === deliveryDateServerErrors.STORE_NOT_FOUND ||
            message === deliveryDateServerErrors.OUT_OF_STOCK_FOR_PICKUP_FROM_STORE
        ) {
            yield put(
                actions.setProductPickupInStoreDeliveryDate({
                    storeNotSelected: true,
                }),
            );
        } else if (message === deliveryDateServerErrors.EXCLUSIVE_PRODUCT) {
            yield put(
                actions.setProductPickupInStoreDeliveryDate({
                    exclusiveOnly: true,
                }),
            );
        }
    }
}

export function* updateProductPriceOnVariantSelection({ actions, selectors, storeName }) {
    const priceData = yield select(selectors.getSelectedVariantPriceData, storeName);
    const otherPrices = yield select(selectors.getOtherPrices, storeName);
    const couponCodeValue = yield select(selectors.getCouponCodeValue, storeName);
    yield put(
        actions.updateProductWithVariantPriceData({
            price: priceData,
            otherPrices,
            couponCodeValue,
        }),
    );
}

export function* showProductVideo() {
    yield put(
        uiActions.openDialog({
            name: 'product-video-popup',
        }),
    );
}

export function* colorVariantsPopupUpdate({ payload: { code } }) {
    const currentSelectedVariantCode = yield select(
        selectors.getPopupSelectedProductCode,
        storeName,
    );

    if (code === currentSelectedVariantCode) return;

    yield put(actions.setLoading(true));
    try {
        const response = yield call(api.loadProductVariant, code);
        yield put(actions.colorVariantsPopupUpdate({ code, details: response }));
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* openPdpRtsPopup({ selectors, storeName }) {
    const selectedQuantity = yield select(selectors.getQuantity, storeName);
    const productCode = yield select(selectors.getProductCode, storeName);

    yield put(uiActions.closeDialogByName(dialogWithProductDetailsName));
    const origin = yield call(getOrigin);
    yield put(globalActions.goto(`${origin}/p/${productCode}`));
    yield take(types.SET_PRODUCT);
    yield take(rtsTypes.QUANTITY_CHANGED);

    if (selectedQuantity > 1) {
        yield put(actions.setInitialQuantity(selectedQuantity));
        yield take(types.REMOVE_INITIAL_QUANTITY);
    }

    yield put(
        rtsActions.showProductStock({
            productCode,
            name: rtsDialogName,
            usePayloadCode: true,
        }),
    );
}

export function* colorVariantsPopupSubmit() {
    const currentSelectedVariantCode = yield select(
        selectors.getPopupSelectedProductCode,
        storeName,
    );

    const selectedVariant = (yield select(
        selectors.getSelectedVariant,
        storeName,
        currentSelectedVariantCode,
    )).variantOption;

    if (selectedVariant.url) {
        pushState(`${selectedVariant.url}?varSel=${currentSelectedVariantCode}`);
    }

    yield put(uiActions.closeDialog());
    yield call(updateProductPriceOnVariantSelection, {
        actions,
        selectors,
        storeName,
    });
}

export function onColorVariantModalOpen({ payload }) {
    if (payload.name === 'color-variants-popup') {
        animationHelpers.simulateResizeEvent();
    }
}

export function* onProductView({ payload }) {
    const saveLastViewedProductsCookie = yield select(configSelectors.saveLastViewedProductsCookie);
    const productCode = payload.productCode;
    if (saveLastViewedProductsCookie && productCode) {
        const lastViewedProducts = getCookie(lastViewedProductsCookieName);
        if (!lastViewedProducts) {
            setCookie(lastViewedProductsCookieName, productCode);
        } else {
            const newLastViewedProductsList = lastViewedProducts
                .split('|')
                .filter(code => code !== productCode);
            newLastViewedProductsList.unshift(productCode);
            setCookie(lastViewedProductsCookieName, newLastViewedProductsList.join('|'));
        }
    }
}

export default [
    takeLeading(types.LOAD_PRODUCT, loadProduct),
    takeLatest(types.SHOW_PRODUCT_VIDEO, showProductVideo),
    takeLatest(types.LOAD_PRODUCT_VARIANT_DATA, loadProductVariantData, {
        actions,
        selectors,
        types,
        storeName,
    }),
    takeLatest(types.OPEN_PDP_RTS, openPdpRtsPopup, { selectors, storeName }),
    takeLatest(types.UPDATE_PRODUCT_WITH_VARIANT_DATA, updateProductPriceOnVariantSelection, {
        actions,
        selectors,
        types,
        storeName,
    }),

    takeLatest(types.COLOR_VARIANTS_POPUP_SELECT, colorVariantsPopupUpdate),
    takeLatest(types.COLOR_VARIANTS_POPUP_SUBMIT, colorVariantsPopupSubmit),
    takeLatest(types.LOAD_PRODUCT_HOME_DELIVERY_DATE, loadProductHomeDeliveryDate),
    takeLatest(
        types.LOAD_PRODUCT_PICKUP_IN_STORE_DELIVERY_DATE,
        loadProductPickUpInStoreDeliveryDate,
    ),
    takeLatest(uiTypes.OPEN_DIALOG, onColorVariantModalOpen),
    takeEvery(types.PRODUCT_VIEWED, onProductView),
];
